import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';
import { cookie, json, URL } from '@ux/util';
import Icon from '@ux/icon';
import Dialog from './dialog';
import trfq from 'react-trfq';
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Inapp help component.
 *
 * This renders the anchor, which then determines what will happen when it is
 * clicked. It will enter open up a dialog, or take you to the specified url.
 *
 * @class InappHelp
 * @public
 */
export class InappHelp extends NamespaceConsumer {
  constructor(props) {
    super(...arguments);

    this.state.open = false;
    this.state.url = props.url;
    this.state.closePrompt = props.closePrompt;

    this.onClick = this.onClick.bind(this);
    this.rememberState = this.rememberState.bind(this);
    this.receiveMessage = this.receiveMessage.bind(this);
    this.dialogRef = React.createRef();
  }

  /**
   * Setup event handlers to remember the state when the page unloads, and check
   * if the dialog should be reopened to a persisted state when it is originally
   * mounted.
   *
   * Also setup receiveMessage to listen for posted messagees to window in order
   * to support conditional closing.
   *
   * @private
   */
  componentDidMount() {
    this.on('beforeunload', this.rememberState);
    this.on('message', this.receiveMessage);

    // If the dialog was open and persisted, reopen it
    if (this.shouldPersist()) {
      this.reopen();
    }
  }

  /**
   * Remove receiveMessage handler from window event listeners.
   *
   * @private
   */
  componentWillUnmount() {
    this.off('message', this.receiveMessage);
    this.off('beforeunload', this.rememberState);
  }

  componentWillReceiveProps(nextProps) {
    if (typeof nextProps.url !== 'undefined' && nextProps.url !== this.state.url) {
      this.setState({ url: nextProps.url });
    }
  }

  /**
   * Render the component.
   *
   * @returns {ReactElement} Help link.
   * @api private
   */
  render() {
    const { label } = this.props;
    const dialog = this.renderDialog();

    //
    // Perform loose feature detection here to see if
    // `props.label` is a <FormattedMessage />
    //
    const isIntl = label.props && label.props.id && label.props.values;
    const displayLabel = !isIntl && this.renderLabel(label) || React.cloneElement(label, {
      id: label.props.id,
      children: (msg) => {
        return this.renderLabel(msg);
      }
    });

    return (
      <div>
        { displayLabel }
        { dialog }
      </div>
    );
  }

  /**
   * Render the label by wrapping it inside an anchor.
   *
   * Temporary disable the click tracking on Help as the traffic2 integration
   * is broken. If this bug is fixed, both `data-tcc-ignore`, the `ref` and
   * custom `logEvent` call in the related `onClick` can be removed. For more
   * details see: https://jira.godaddy.com/browse/GX-2697
   *
   * @param {String|Node} label Potentially unwrapped this.props.label.
   * @returns {React.Element} React Element representing an unwrapped label.
   */
  renderLabel(label) {
    const { eid, eidPrefix, tooltip } = this.props;
    const { url } = this.state;
    let classes = this.namespace('nav-link');
    let labelIsIcon = false;
    if (label.type === Icon) {
      classes = this.classNames(
        classes,
        this.namespace('uxicon', 'uxicon-' + label.props.name)
      );
      labelIsIcon = true;
    }

    return (
      <a
        href={ url }
        data-tcc-ignore
        onClick={ this.onClick }
        ref={ c => { this.link = c; } }
        data-eid={ `${eidPrefix}.${eid}` }
        className={ classes }
        aria-hidden={ labelIsIcon }
        title={ labelIsIcon ? tooltip : void 0 }>
        { !labelIsIcon && label }
      </a>
    );
  }

  /**
   * Render the inapp help dialog.
   *
   * @returns {ReactElement} Dialog.
   * @private
   */
  renderDialog() {
    if (!this.state.open) {
      return null;
    }

    return <Dialog
      ref={ this.dialogRef }
      url={ this.state.url }
      onClose={ this.close.bind(this) }
      { ...this.props } />;
  }

  /**
   * Handle posted messages from Iframe to support conditional closing
   *
   * @param {Event} event Posted message event.
   * @private
   */
  receiveMessage(event) {
    if (!this.dialogRef || !this.dialogRef.current
      || this.dialogRef.current.frame.dom.contentWindow !== event.source) {
      return;
    }

    if (event.data === 'inappHelpEnableClosePrompt') {
      this.setState({ closePrompt: true });
    } else if (event.data === 'inappHelpClose') {
      this.setState({ closePrompt: false });
      this.closeNoPrompt();
    }
  }

  /**
   * Handle click event for help anchor.
   *
   * TODO: remove the custom traffic2 event logging if `data-tcc-ignore` is
   * removed from the JSX.
   *
   * @param {Event} event Mouse event.
   * @returns {Boolean} Return false to prevent event propagation.
   * @private
   */
  onClick(event) {
    const eid = this.link && this.link.getAttribute('data-eid');
    if (eid) {
      trfq.logEvent(eid, 'click');
    }

    if (this.shouldOpen()) {
      this.open();
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }

  /**
   * If the inapp help dialog should be opened.
   *
   * @returns {Boolean} Should the dialog open.
   * @private
   */
  shouldOpen() {
    const { enabled } = this.props;

    // not enabled for link
    if (!enabled) {
      return false;
    }

    return !this.isMobile;
  }

  /**
   * Open the inapp help dialog on the page.
   *
   * @param {String} url - url to open up to (optional)
   * @public
   */
  open(url) {
    this.setState({
      open: true,
      url: url || this.state.url
    });
  }

  /**
   * Close the inapp help dialog on the page.
   *
   * If message posted to enable prompting then notifies frame to prompt for close.
   *
   * @param {Event} event The Event
   * @returns {undefined} Nothing
   * @public
   */
  close(event) {
    if (this.state.closePrompt) {
      return this.notifyFrameToPromptClose(event);
    }

    this.closeNoPrompt();
  }

  /**
   * Posts message to IFrame content window to prompt for close
   *
   * @param {Event} event The Event
   * @private
   */
  notifyFrameToPromptClose(event) {
    event.preventDefault();
    event.stopPropagation();

    const dialog = this.dialogRef.current;
    if (dialog && dialog.frame.dom.contentWindow) {
      dialog.frame.dom.contentWindow.postMessage('inappHelpPromptClose', '*');
    }
  }

  /**
   * Closes inapp help dialog on the page without prompting.
   *
   * @private
   */
  closeNoPrompt() {
    this.setState({ open: false });
    this.forgetState();
    this.initSurvey();
  }

  /**
   * Toggle the inapp help dialog on the page.
   *
   * @public
   */
  toggle() {
    if (this.state.open) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Reopen the inapp help dialog based on persisted state that gets saved when
   * the page is unloaded. If no state is found then the dialog is not reopend.
   *
   * @public
   */
  reopen() {
    const state = this.getPersistedState();
    if (!state) {
      return;
    }

    // Append last=true query param to the url
    // This tells the help system to navigate to where the user was last
    let url = new URL(state.url, true);
    url.query = url.query || {};
    url.query.last = true;
    url = url.toString();

    // Reopen the dialog to the new url
    this.open(url);

    // TODO: not sure if this can be done now? The dialog has to already be
    // rendered for this to work. This needs to happen after it has rendered.
    // Does react provide us a hook for this?
    const dialog = this.dialogRef.current;
    if (dialog) {
      dialog.setPosition(state.x, state.y);
      dialog.setHeight(state.height);
    }
  }

  /**
   * Retrieve the persisted dialog state from cookie.
   * If no state was found, undefined is returned.
   *
   * @returns {Object} Persisted state.
   * @public
   */
  getPersistedState() {
    const data = cookie.get(this.props.stateStorageKey);
    if (data) {
      return json.parse(data);
    }

    return;
  }

  /**
   * Return whether the dialog should be reopend on the page. This is determined
   * by whether any persisted state exists, and if it is fresh based on the
   * timestamp.
   *
   * @returns {Boolean} Should state be persisted
   * @public
   */
  shouldPersist() {
    if (typeof window === 'undefined' || window !== window.top) { return false; }

    const state = this.getPersistedState();
    if (!state || !state.url) {
      return false;
    }

    // timestamp is within range
    const now = Date.now();
    if (now - state.timestamp > this.props.persistThreshold) {
      return false;
    }

    return true;
  }

  /**
   * Returns root domain of window location hostname if available, else empty string.
   *
   * @returns {string} Root domain of the window
   * @private
   */
  getRootDomain() {
    if (!window || !window.location) { return ''; }

    const hostname = window.location.hostname;
    if (hostname) {
      const parts = hostname.split('.');
      const domain = parts.slice(parts.length - 2).join('.');
      return '.' + domain;
    }

    return '';
  }

  /**
   * Save the current dialog state to cookie.
   * This includes things such as the dialog position, what domain it was for,
   * and the timestamp so that it can be used to verify it is fresh.
   *
   * @public
   */
  rememberState() {
    if (typeof window === 'undefined' || window !== window.top) { return; }

    const dialog = this.dialogRef.current;
    if (!dialog) {
      this.forgetState();
      return;
    }

    const pos = dialog.getPosition() || { };
    const height = dialog.getHeight();
    const host = window.location.host;
    const url = this.state.url; // can't access the value of the iframe
    const now = Date.now();
    const data = {
      timestamp: now,
      host: host,
      url: url,
      x: pos.x,
      y: pos.y,
      height: height
    };

    const expDate = new Date(Date.now() + this.props.persistThreshold);

    cookie.set(this.props.stateStorageKey, json.stringify(data), {
      domain: this.getRootDomain(),
      path: '/',
      expires: expDate.toUTCString()
    });
  }

  /**
   * Remove the current dialog state from cookies.
   *
   * @public
   */
  forgetState() {
    cookie.set(this.props.stateStorageKey, '', {
      domain: this.getRootDomain(),
      path: '/',
      expires: new Date(0)
    });
  }

  /**
   * Reload Qualtrics via Javascript API so that intercepts can be reevaluated.
   *
   * @param {Object} QSI Qualtrics Javascript object
   * @private
   */
  reloadQualtrics(QSI) {
    if (typeof QSI !== 'undefined' && typeof QSI.API !== 'undefined') {
      try {
        QSI.API.unload();
        QSI.API.load();
      } catch (e) { /* noop */ }
    }
  }

  /**
   * Writes survey cookie if not present and reloads qualtrics
   *
   * @public
   */
  initSurvey() {
    if (typeof window === 'undefined' || window !== window.top) { return; }

    const surveyCookie = cookie.get(this.props.surveyCookieName);
    if (surveyCookie) { return; }

    const surveyCookieValue = '1';
    const expDate = new Date(Date.now() + this.props.surveyCookieExpiration);

    cookie.set(this.props.surveyCookieName, surveyCookieValue, {
      domain: this.getRootDomain(),
      path: '/',
      expires: expDate.toUTCString()
    });

    this.reloadQualtrics(window.QSI);
  }
}

/**
 * Default properties for the component. The labels are only provided as a
 * fallback and should be set to localized content.
 *
 * @type {Object}
 * @public
 */
InappHelp.defaultProps = {
  eidPrefix: 'uxp.hyd.app_header',
  eid: 'shared.help.header_link.link.click',
  label: 'Help',
  title: 'Help',
  tooltip: 'Help',
  enabled: true,
  persistThreshold: 30 * 60 * 1000,    // 30 minutes
  stateStorageKey: 'inapp-help-state',
  closePrompt: false,
  surveyCookieName: 'surveyme',
  surveyCookieExpiration: 72 * 60 * 60 * 1000    // 3 days
};

/**
 * Required properties.
 *
 * @type {Object}
 * @public
 */
InappHelp.propTypes = {
  ...NamespaceShape,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]).isRequired,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]).isRequired,
  tooltip: PropTypes.string,
  eidPrefix: PropTypes.string,
  eid: PropTypes.string,
  url: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  persistThreshold: PropTypes.number,
  stateStorageKey: PropTypes.string,
  closePrompt: PropTypes.bool
};

export default withNamespace(InappHelp);
